/**
 * Care Interaction schemas — the "off-routine collaboration designer" that the
 * shared `InterveneDialog` and the `POST /business/care/interventions` endpoint
 * share. A Care Interaction is a SCHEDULED collaboration event between the
 * business team and one talent (a call, a meeting, a message…), tracked to
 * completion across both sides, plus an optional ad-hoc check-in request.
 *
 * One taxonomy (`careInterventionTypeSchema`, the interaction types) classified
 * by effect (`CARE_INTERVENTION_EFFECT`: talent_touch / log_only), a create
 * payload (`createCareInterventionSchema`) with the interaction's `scheduledAt`
 * + optional `locationOrLink`, and a close payload
 * (`resolveCareInterventionSchema`: outcome + the "Ata"/minutes). The legacy
 * `schedule` (recurrence) field is retained for the nightly sweep but is not
 * surfaced by the redesigned dialog.
 *
 * Error messages are i18n keys (per `@tonnie/schemas` convention), resolved at
 * the call site via `t(error.message)`.
 */

import { z } from 'zod';

import { interventionPrioritySchema } from './intervention';
import { careBatchSelectionSchema } from './lens-list';

// ═══════════════════════════════════════════════════════════════════════════
// Care INTERACTION model — the off-routine collaboration designer. Visibility
// is derived from the type's effect class (talent-facing types are always part
// of a scheduled interaction the talent attends; `internal_note` never reaches
// the talent). Legacy rows created under the prior 8-type taxonomy still render
// via a display-side fallback (the `type` column is a plain varchar).
// ═══════════════════════════════════════════════════════════════════════════

/** The interaction types an admin/team-member picks from. */
export const careInterventionTypeSchema = z.enum([
  'call', // Phone call with the talent
  'video_call', // Video call (optional meeting link)
  'in_person_meeting', // In-person meeting (optional location)
  'message', // A written outreach (can dispatch the coaching.message email)
  'internal_note', // Internal-only record — NEVER reaches the talent
  'request_checkin', // Spawns a real ad-hoc Care checkpoint for the talent
]);
export type CareInterventionType = z.infer<typeof careInterventionTypeSchema>;

/**
 * Effect class per type — the single source of truth for whether an interaction
 * reaches the talent. The backend gates `deliverTalentTouch` on this, and the
 * dialog renders the matching helper line, so FE + BE can never disagree.
 *   - talent_touch → the talent sees the scheduled interaction (SCORE-FREE)
 *   - log_only     → internal record, NEVER a talent surface
 */
export type CareInterventionEffect = 'log_only' | 'talent_touch';

export const CARE_INTERVENTION_EFFECT: Record<CareInterventionType, CareInterventionEffect> = {
  call: 'talent_touch',
  video_call: 'talent_touch',
  in_person_meeting: 'talent_touch',
  message: 'talent_touch',
  internal_note: 'log_only',
  request_checkin: 'talent_touch',
};

/** A talent-facing type reaches the talent; `internal_note` never does. */
export function canTouchTalent(type: CareInterventionType): boolean {
  return CARE_INTERVENTION_EFFECT[type] !== 'log_only';
}

/** Optional recurrence. When omitted, the intervention is a one-shot logged now. */
export const careInterventionScheduleSchema = z.object({
  frequency: z.enum(['once', 'weekly', 'biweekly', 'monthly']),
  nextDueAt: z.iso.datetime({ message: 'business.care.intervention.errors.invalidDate' }),
  recurrenceUntil: z.iso.datetime().optional(),
});
export type CareInterventionSchedule = z.infer<typeof careInterventionScheduleSchema>;

const optionalCareText = (max: number, key: string) =>
  z
    .string()
    .trim()
    .max(max, { message: key })
    .optional()
    .or(z.literal('').transform(() => undefined));

/** Fields the Intervene dialog collects (the per-talent context is supplied by props). */
export const careInterventionFieldsSchema = z.object({
  type: careInterventionTypeSchema,
  title: z
    .string()
    .trim()
    .min(1, { message: 'business.care.intervention.errors.titleRequired' })
    .max(200, { message: 'business.care.intervention.errors.titleTooLong' }),
  description: optionalCareText(2000, 'business.care.intervention.errors.descriptionTooLong'),
  // Unified on the canonical low/medium/high/urgent priority (no default here —
  // the form sets it via defaultValues, keeping Input/Output types identical).
  priority: interventionPrioritySchema,
  notes: optionalCareText(2000, 'business.carePage.errors.notesTooLong'),
  // When the interaction is scheduled to take place (drives the talent's
  // "upcoming" surface + the inbox overdue flag). Optional → defaults to now.
  scheduledAt: z.iso
    .datetime({ message: 'business.care.intervention.errors.invalidDate' })
    .optional(),
  // Optional meeting link (video_call) or location (in_person_meeting).
  locationOrLink: optionalCareText(500, 'business.care.intervention.errors.descriptionTooLong'),
  // Optional owner — a team member the interaction is delegated to (their userId).
  assignedTo: z.uuid().optional(),
  // Retained for the nightly recurrence sweep; not surfaced by the dialog.
  schedule: careInterventionScheduleSchema.optional(),
});

/** RHF form schema for the shared InterveneDialog. */
export const interveneFormSchema = careInterventionFieldsSchema;
export type InterveneFormInput = z.infer<typeof interveneFormSchema>;

/**
 * API payload — one talent, on the journey the interaction is about. A talent
 * may hold several journeys with the company; the card or drawer that opened
 * the dialog names its own (X8).
 */
export const createCareInterventionSchema = careInterventionFieldsSchema.extend({
  talentId: z.uuid(),
  journeyId: z.uuid(),
});
export type CreateCareInterventionInput = z.infer<typeof createCareInterventionSchema>;

/**
 * `POST /business/care/interventions/batch` — one plan for a selection (D3):
 * the ticked engagements, or every one matching the list's filters. The server
 * reaches only those that need an intervention, each on its own engagement.
 */
export const careInterventionBatchSchema = z.object({
  selection: careBatchSelectionSchema,
  intervention: careInterventionFieldsSchema,
});
export type CareInterventionBatchInput = z.infer<typeof careInterventionBatchSchema>;

/** What a batch did: interactions created, and the engagements that failed. */
export interface CareInterventionBatchResult {
  created: number;
  failed: number;
}

/**
 * Edit payload — patch the DESCRIPTIVE fields of an existing (still-open)
 * interaction: type / title / description / priority / meeting-link. Scheduling
 * and ownership are NOT here — they keep their dedicated, side-effecting routes
 * (`reschedule` re-stamps the reminder; `reassign` notifies the new owner). All
 * fields optional (a partial PATCH); `title` keeps its non-empty rule when sent.
 */
export const updateCareInterventionSchema = z
  .object({
    type: careInterventionTypeSchema.optional(),
    title: z
      .string()
      .trim()
      .min(1, { message: 'business.care.intervention.errors.titleRequired' })
      .max(200, { message: 'business.care.intervention.errors.titleTooLong' })
      .optional(),
    description: optionalCareText(2000, 'business.care.intervention.errors.descriptionTooLong'),
    priority: interventionPrioritySchema.optional(),
    locationOrLink: optionalCareText(500, 'business.care.intervention.errors.descriptionTooLong'),
  })
  .refine((v) => Object.values(v).some((field) => field !== undefined), {
    message: 'business.care.intervention.errors.titleRequired',
  });
export type UpdateCareInterventionInput = z.infer<typeof updateCareInterventionSchema>;

/** Close outcome — what actually happened with the scheduled interaction. */
export const careInterventionOutcomeSchema = z.enum([
  'completed', // it happened as planned
  'no_show', // the talent didn't attend
  'rescheduled', // moved to another time
  'declined', // the talent declined
]);
export type CareInterventionOutcome = z.infer<typeof careInterventionOutcomeSchema>;

/**
 * Close (mark-done) payload — the outcome + the "Ata" (minutes / what was
 * discussed). The Ata is INTERNAL-only (never shown to the talent) and stored
 * Copilot-ready for a future release.
 */
export const resolveCareInterventionSchema = z.object({
  outcome: careInterventionOutcomeSchema,
  ata: optionalCareText(8000, 'business.care.intervention.errors.resolutionTooLong'),
});
export type ResolveCareInterventionInput = z.infer<typeof resolveCareInterventionSchema>;

/** Reschedule payload — move the interaction to a new date/time. */
export const rescheduleCareInterventionSchema = z.object({
  scheduledAt: z.iso.datetime({ message: 'business.care.intervention.errors.invalidDate' }),
});
export type RescheduleCareInterventionInput = z.infer<typeof rescheduleCareInterventionSchema>;

/** Reassign payload — delegate (or clear) the owner. `null` clears the owner. */
export const reassignCareInterventionSchema = z.object({
  assignedTo: z.uuid().nullable(),
});
export type ReassignCareInterventionInput = z.infer<typeof reassignCareInterventionSchema>;

/** Add a comment to the interaction's collaboration thread. */
export const careInterventionCommentSchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, { message: 'business.care.intervention.errors.titleRequired' })
    .max(4000, { message: 'business.care.intervention.errors.resolutionTooLong' }),
});
export type CareInterventionCommentInput = z.infer<typeof careInterventionCommentSchema>;

/**
 * `GET /business/care/interventions/inbox` — one page of the company's
 * interactions, searched, ordered and counted by the server (P2.1: the list
 * read the first 100 and searched and grouped them in the browser).
 *
 *   status  open (default) | done | all
 *   mine    only the ones delegated to the caller
 *   q       the talent's name or email contains this
 *   order   due    — by date, the late ones first (the default; Needs attention)
 *           recent — newest first (the flat list)
 *           talent — grouped: talents with a late one first, then by name, and
 *                    each talent's interactions newest first
 */
export const CARE_INBOX_ORDERS = ['due', 'recent', 'talent'] as const;
export type CareInboxOrder = (typeof CARE_INBOX_ORDERS)[number];

export const careInboxQuerySchema = z.object({
  status: z.enum(['open', 'done', 'all']).default('open'),
  mine: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  q: z.string().trim().max(200).optional(),
  order: z.enum(CARE_INBOX_ORDERS).default('due'),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type CareInboxQuery = z.infer<typeof careInboxQuerySchema>;
